import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { trimmed } from './_checks.js';
import { draftFields } from './_shared.js';
import { activityIssue } from './types.js';

const issue = activityIssue('image');

/**
 * The keys an image node's setup owns: the content asset it shows under `slugKey`, the URL a
 * served story resolves from that slug under `urlKey`, the image's alt text under `altKey`, and
 * the line shown under it under `captionKey`.
 *
 * A URL stored under `urlKey` with no slug is the fallback for a picture that is not a content
 * asset: it still shows if it is an https URL or a path on the site, but a slug is how an image is
 * authored.
 */
export const IMAGE_ACTIVITY = {
  slugKey: 'contentAssetSlug',
  urlKey: 'imageUrl',
  altKey: 'alt',
  captionKey: 'caption',
} as const;

/**
 * Whether a fallback URL is one the player shows: an https URL, or a path on the site it is served
 * from. The player's content security policy (`img-src 'self' data: https:`) also loads `data:`,
 * but a picture written into the story is sent with every play, so it belongs in the content assets.
 *
 * The raw value is parsed, because the `<img>` gets the raw value, not a trimmed one.
 */
const isHttpsOrSitePath = (value: unknown): boolean => {
  if (typeof value !== 'string' || !/^[ \t\n\r]*(?:[a-z][a-z\d+.-]*:|\/)/i.test(value)) {
    return false;
  }
  try {
    return new URL(value, 'https://player.invalid/').protocol === 'https:';
  }
  catch {
    return false;
  }
};

/** A picture as a step in the story, with a caption under it, that the player looks at and taps past. */
export const image: ActivityDefinition = {
  type: 'image',
  summary: 'A picture with a caption under it, which the player looks at and taps past.',
  producible: false,
  setup: z.looseObject({
    ...draftFields,
    [IMAGE_ACTIVITY.slugKey]: z.string().optional(),
    [IMAGE_ACTIVITY.urlKey]: z.string().optional(),
    [IMAGE_ACTIVITY.altKey]: z.string().optional(),
    [IMAGE_ACTIVITY.captionKey]: z.string().optional(),
  }),
  check: (setup, ctx) => {
    const path = [...ctx.path, 'setup'];
    const issues = [];
    const url = trimmed(setup[IMAGE_ACTIVITY.urlKey]);
    if (!trimmed(setup[IMAGE_ACTIVITY.slugKey])) {
      if (!url) {
        issues.push(issue('source', 'No image is picked, so there is nothing to show.', [...path, IMAGE_ACTIVITY.slugKey], ctx));
      }
      else if (!isHttpsOrSitePath(setup[IMAGE_ACTIVITY.urlKey])) {
        issues.push(issue('url', 'The image URL is not an https address or a path on the site, so the player will not show it. Use one of those, or pick the picture from the content assets instead.', [...path, IMAGE_ACTIVITY.urlKey], ctx, setup[IMAGE_ACTIVITY.urlKey]));
      }
    }
    if (!trimmed(setup[IMAGE_ACTIVITY.altKey])) {
      issues.push(issue('alt', 'The image has no alt text.', [...path, IMAGE_ACTIVITY.altKey], ctx, setup[IMAGE_ACTIVITY.altKey]));
    }
    if (!trimmed(setup[IMAGE_ACTIVITY.captionKey])) {
      issues.push(issue('caption', 'The image has no caption under it.', [...path, IMAGE_ACTIVITY.captionKey], ctx, setup[IMAGE_ACTIVITY.captionKey]));
    }
    return issues;
  },
};
